import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { hospitals } from '../../data/hospitals';

const SPECIALTY_FILTERS_UZ = [
  'Barchasi',
  "Ko'z kasalliklari",
  'Plastik jarrohlik',
  'Teri parvarishi',
  'Stomatologiya',
  'Saraton davolash',
  'Anti-aging',
  'Urologiya',
  'Ginekologiya',
  'Ortopediya',
];

const SPECIALTY_FILTERS_RU = [
  'Все',
  'Офтальмология',
  'Пластическая хирургия',
  'Уход за кожей',
  'Стоматология',
  'Лечение рака',
  'Антивозрастное лечение',
  'Урология',
  'Гинекология',
  'Ортопедия',
];

// 키워드 → 병원 매핑 (우즈벡어 + 러시아어 동의어 포함)
const KEYWORD_MAP: { keywords: string[]; hospitalIds: string[] }[] = [
  {
    keywords: [
      // 치과 / 임플란트
      'tish', 'implant', 'stomatolog', 'toj', 'ortodont', 'oqartirish', 'laminat',
      'зуб', 'имплант', 'стоматолог', 'коронк', 'ортодонт', 'отбелив', 'ламинат',
      'dental', 'diş',
    ],
    hospitalIds: ['moon-dental-hospital'],
  },
  {
    keywords: [
      // 눈 / 라식
      "ko'z", 'lasik', 'lasek', 'smile', 'katarakta', 'ko\'rish',
      'глаз', 'ласик', 'ласек', 'катаракт', 'зрени',
      'eye', 'vision',
    ],
    hospitalIds: ['kangnam-eye-clinic'],
  },
  {
    keywords: [
      // 성형외과
      'plastik', 'jarrohlik', 'burun', 'ko\'krak', 'liposaktsiya', 'yuz ko\'tarish',
      'пластик', 'хирурги', 'ринопластик', 'маммопластик', 'липосакци', 'подтяжк',
      'rhinoplasty', 'facelift',
    ],
    hospitalIds: ['instar-plastic-surgery', 'cellup-plastic-surgery'],
  },
  {
    keywords: [
      // 피부과
      'teri', 'lazer', 'botoks', 'filler', 'akne', 'pigment', 'lifting',
      'кожа', 'лазер', 'ботокс', 'филлер', 'акне', 'пигмент', 'лифтинг',
      'skin', 'derma',
    ],
    hospitalIds: ['sajeomsaui-clinic'],
  },
  {
    keywords: [
      // 줄기세포 / 항노화
      'ildiz hujayra', 'anti-aging', 'nad', 'regenerativ', 'immun', 'yoshartirish',
      'стволов', 'антивозраст', 'регенератив', 'иммунотерапи', 'омолаж',
      'stem cell',
    ],
    hospitalIds: ['cellpia-clinic', 'mycell-clinic'],
  },
  {
    keywords: [
      // 암 / 종양
      'saraton', 'onkolog', 'kimyoterapiya', 'o\'sma',
      'рак', 'онколог', 'химиотерапи', 'опухол',
      'cancer', 'tumor',
    ],
    hospitalIds: ['suncheonhyang-bucheon-hospital'],
  },
  {
    keywords: [
      // 비뇨기과
      'urologiya', 'prostata', 'erkaklar', 'siydik',
      'урологи', 'простат', 'мужск', 'мочевыводящ',
      'urology', 'prostate',
    ],
    hospitalIds: ['janis-urology'],
  },
  {
    keywords: [
      // 항문외과 / 치질
      'gemorroi', 'anal', 'kolorektal', 'bavasir',
      'геморрой', 'анальн', 'колоректальн',
      'hemorrhoid',
    ],
    hospitalIds: ['fatima-surgery'],
  },
  {
    keywords: [
      // 종합병원 / 산부인과 / 정형외과
      'ginekolog', 'ortopediya', 'umurtqa', 'kompleks tekshiruv', 'pediatriya',
      'гинеколог', 'ортопеди', 'позвоночник', 'комплексн', 'педиатри',
      'gynecology', 'orthopedic',
    ],
    hospitalIds: ['ewha-hospital', 'suncheonhyang-bucheon-hospital'],
  },
];

export default function HospitalsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const isRu = i18n.language === 'ru';

  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [activeFilter, setActiveFilter] = useState(0);

  useEffect(() => {
    const q = searchParams.get('search') || '';
    setSearchQuery(q);
  }, [searchParams]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim()) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  const filters = isRu ? SPECIALTY_FILTERS_RU : SPECIALTY_FILTERS_UZ;

  const filteredHospitals = hospitals.filter((hospital) => {
    const name = isRu ? hospital.nameRu : hospital.name;
    const specialties = isRu ? hospital.specialtiesRu : hospital.specialties;
    const address = isRu ? hospital.addressRu : hospital.address;
    const description = isRu ? hospital.descriptionRu : hospital.description;

    const allSpecialties = [...hospital.specialties, ...hospital.specialtiesRu];
    const allNames = `${hospital.name} ${hospital.nameRu}`;
    const allDescriptions = `${hospital.description} ${hospital.descriptionRu}`;

    const q = searchQuery.toLowerCase();

    const matchesKeywordMap = KEYWORD_MAP.some(
      ({ keywords, hospitalIds }) =>
        keywords.some((kw) => q.includes(kw.toLowerCase())) &&
        hospitalIds.includes(hospital.id)
    );

    const matchesSearch =
      q === '' ||
      name.toLowerCase().includes(q) ||
      specialties.some((s) => s.toLowerCase().includes(q)) ||
      address.toLowerCase().includes(q) ||
      description.toLowerCase().includes(q) ||
      allSpecialties.some((s) => s.toLowerCase().includes(q)) ||
      allNames.toLowerCase().includes(q) ||
      allDescriptions.toLowerCase().includes(q) ||
      matchesKeywordMap;

    const matchesFilter =
      activeFilter === 0 ||
      specialties.some((s) =>
        s.toLowerCase().includes(filters[activeFilter].toLowerCase())
      );

    return matchesSearch && matchesFilter;
  });

  useEffect(() => {
    const isUz = i18n.language === 'uz';
    const title = isUz
      ? "Shifoxonalar - MediVoyage"
      : 'Больницы - MediVoyage';
    const description = isUz
      ? "Koreyaning eng yaxshi shifoxonalari ro'yxati. Plastik jarrohlik, ko'z kasalliklari, stomatologiya va boshqa tibbiy xizmatlar."
      : 'Список лучших больниц Кореи. Пластическая хирургия, офтальмология, стоматология и другие медицинские услуги.';

    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.medivoyageuz.com';

    const schemaItemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: isUz ? "Shifoxonalar ro'yxati" : 'Список больниц',
      description,
      numberOfItems: hospitals.length,
      itemListElement: hospitals.map((hospital, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Hospital',
          name: isUz ? hospital.name : hospital.nameRu,
          url: `${siteUrl}/hospital/${hospital.id}`,
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'KR',
            addressLocality: 'Seoul',
            streetAddress: isUz ? hospital.address : hospital.addressRu,
          },
          medicalSpecialty: isUz ? hospital.specialties : hospital.specialtiesRu,
        },
      })),
    };

    let scriptItemList = document.getElementById('schema-itemlist');
    if (!scriptItemList) {
      scriptItemList = document.createElement('script');
      scriptItemList.id = 'schema-itemlist';
      (scriptItemList as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(scriptItemList);
    }
    scriptItemList.textContent = JSON.stringify(schemaItemList);

    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = `${siteUrl}/hospitals`;

    return () => {
      scriptItemList?.remove();
    };
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative pt-20">
        <div className="relative h-64 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/635ff42a7b9de9520b8724aaa7653a01.jpg"
            alt="Hospitals"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <span className="inline-block px-4 py-1.5 bg-teal-500/30 border border-teal-400/50 text-teal-200 text-xs font-semibold rounded-full mb-4 tracking-widest uppercase">
              {isRu ? 'Партнёрские клиники' : 'Hamkor klinikalar'}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
              {t('nav_hospitals')}
            </h1>
            <p className="text-white/80 text-base max-w-xl">
              {isRu
                ? 'Все партнёрские больницы Южной Кореи в одном месте'
                : 'Janubiy Koreyaning barcha hamkor shifoxonalari bir joyda'}
            </p>
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-base"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={
                isRu
                  ? 'Поиск по названию, специализации или адресу...'
                  : "Nom, mutaxassislik yoki manzil bo'yicha qidirish..."
              }
              className="w-full pl-11 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gray-50"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            )}
          </div>

          {/* Filter Chips */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {filters.map((filter, index) => (
              <button
                key={index}
                onClick={() => setActiveFilter(index)}
                className={`px-4 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all cursor-pointer ${
                  activeFilter === index
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Result Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            {searchQuery
              ? isRu
                ? `«${searchQuery}» — найдено ${filteredHospitals.length} больниц`
                : `«${searchQuery}» — ${filteredHospitals.length} ta shifoxona topildi`
              : isRu
              ? `Найдено ${filteredHospitals.length} больниц`
              : `${filteredHospitals.length} ta shifoxona topildi`}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <i className="ri-information-line"></i>
            <span>
              {isRu
                ? 'Нажмите на карточку для подробностей'
                : "Batafsil ma'lumot uchun kartani bosing"}
            </span>
          </div>
        </div>

        {/* Hospital Grid */}
        {filteredHospitals.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHospitals.map((hospital) => {
              const name = isRu ? hospital.nameRu : hospital.name;
              const address = isRu ? hospital.addressRu : hospital.address;
              const specialties = isRu ? hospital.specialtiesRu : hospital.specialties;

              return (
                <div
                  key={hospital.id}
                  onClick={() => navigate(`/hospital/${hospital.id}`)}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={hospital.image}
                      alt={name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute bottom-3 left-3">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-600/90 text-white text-xs font-medium rounded-full">
                        <i className="ri-hospital-line text-xs"></i>
                        {isRu ? 'Партнёр' : 'Hamkor'}
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-1.5 leading-snug group-hover:text-teal-600 transition-colors">
                      {name}
                    </h3>
                    <div className="flex items-start gap-1.5 mb-3">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-line text-teal-500 text-xs"></i>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{address}</p>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {specialties.slice(0, 3).map((specialty, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-md border border-teal-100"
                        >
                          {specialty}
                        </span>
                      ))}
                      {specialties.length > 3 && (
                        <span className="px-2.5 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-md">
                          +{specialties.length - 3}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-phone-line text-xs"></i>
                        </div>
                        <span>{hospital.phone}</span>
                      </div>
                      <span className="flex items-center gap-1 text-xs font-semibold text-teal-600 group-hover:gap-2 transition-all">
                        {t('view_details')}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-hospital-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-base font-medium mb-2">
              {isRu ? 'Больницы не найдены' : 'Shifoxona topilmadi'}
            </p>
            <p className="text-gray-400 text-sm">
              {isRu
                ? 'Попробуйте изменить параметры поиска'
                : "Qidiruv parametrlarini o'zgartiring"}
            </p>
            <button
              onClick={() => {
                handleSearchChange('');
                setActiveFilter(0);
              }}
              className="mt-4 px-4 py-2 text-sm text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {isRu ? 'Сбросить фильтры' : 'Filtrlarni tozalash'}
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
